import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import AppLayout from "../components/AppLayout";
import { toast } from "react-toastify";
import SelectField from "../components/ui/SelectField";
import { TestTube2, Save, Search } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

const ParamsPage = () => {
  const [vr, setVR] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUp, setLoadingUp] = useState(false);
  const [selectedEspecie, setSelectedEspecie] = useState(null);
  const [selectedExamen, setSelectedExamen] = useState(null);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [selectedReactivo, setSelectedReactivo] = useState(null);
  const [examenes, setExamenes] = useState([]);
  const [especies, setEspecies] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [reactivos, setReactivos] = useState([]);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [examenesRes, especiesRes, equiposRes, reactivosRes] = await Promise.all([
          axios.get(`${API_URL}/util/examenes_caratula`),
          axios.get(`${API_URL}/util/especies`),
          axios.get(`${API_URL}/util/equipos`),
          axios.get(`${API_URL}/util/reactivos`),
        ]);
        setExamenes(examenesRes.data);
        setEspecies(especiesRes.data);
        setEquipos(equiposRes.data);
        setReactivos(reactivosRes.data);
      } catch (error) {
        console.error("Error cargando datos:", error);
      }
    };
    fetchData();
  }, []);

  const handleConsultar = async () => {
    if (!selectedEspecie || !selectedExamen || !selectedEquipo || !selectedReactivo) {
      toast.error("Debes seleccionar especie, examen, equipo y reactivo");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/referencia`, {
        params: {
          especie: selectedEspecie.value,
          examen: selectedExamen.value,
          equipo: selectedEquipo.value,
          reactivo: selectedReactivo.value,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setVR(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Error al consultar los valores de referencia");
    } finally {
      setLoading(false);
    }
  };

  const actualizarAnalito = async ({ especie, examen, equipo, reactivo, analito_cod, minimo, maximo, valor_esperado }) => {
    if (!especie || !examen) { toast.error("Debes seleccionar especie y examen"); return; }
    try {
      setLoadingUp(true);
      await axios.post(`${API_URL}/referencia/update`,
        { especie, examen, equipo, reactivo, analito_cod, minimo: minimo || null, maximo: maximo || null, valor_esperado: valor_esperado || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Analito ${analito_cod} actualizado`);
    } catch (error) {
      toast.error(`No se pudo actualizar el analito ${analito_cod}`);
    } finally {
      setLoadingUp(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Cabecera */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 rounded-xl">
            <TestTube2 className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Valores de referencia</h1>
            <p className="text-sm text-gray-500">Configura los rangos de referencia para cada analito</p>
          </div>
        </div>

        {/* Filtros de consulta */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-5">
          <p className="text-sm font-semibold text-gray-700 mb-4">Seleccionar parámetros</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Especie</label>
              <SelectField
                name="especie"
                value={selectedEspecie?.value || ""}
                onChange={(e) => setSelectedEspecie({ value: e.target.value })}
                disabled={loading}
                placeholder="Selecciona especie"
                options={especies}
                valueKey="codigo"
                displayFn={(es) => `${es.codigo} - ${es.detalle}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Examen</label>
              <SelectField
                name="examen"
                value={selectedExamen?.value || ""}
                onChange={(e) => setSelectedExamen({ value: e.target.value })}
                disabled={loading}
                placeholder="Selecciona examen"
                options={examenes}
                valueKey="examen_cod"
                displayFn={(ex) => `${ex.examen_cod} - ${ex.nombre}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Equipo</label>
              <SelectField
                name="equipo"
                value={selectedEquipo?.value || ""}
                onChange={(e) => setSelectedEquipo({ value: e.target.value })}
                disabled={loading}
                placeholder="Selecciona equipo"
                options={equipos}
                valueKey="equipo_cod"
                displayFn={(eq) => `${eq.equipo_cod} - ${eq.equipo_desc}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Reactivo</label>
              <SelectField
                name="reactivo"
                value={selectedReactivo?.value || ""}
                onChange={(e) => setSelectedReactivo({ value: e.target.value })}
                disabled={loading}
                placeholder="Selecciona reactivo"
                options={reactivos}
                valueKey="reactivo_cod"
                displayFn={(re) => `${re.reactivo_cod} - ${re.reactivo_deta}`}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={handleConsultar} disabled={loading} className="gap-2">
              <Search className="h-4 w-4" />
              {loading ? "Consultando..." : "Consultar"}
            </Button>
          </div>
        </div>

        {/* Tabla */}
        {vr.length > 0 && (
          <div className="bg-white rounded-2xl border border-brand-100 shadow-card overflow-hidden">
            <div className="px-5 py-4 border-b border-brand-50">
              <p className="text-sm font-semibold text-gray-700">
                Analitos encontrados <span className="text-brand-600">({vr.length})</span>
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-brand-gradient text-white">
                    {["Código", "Analito", "Unidades", "Mínimo", "Máximo", "Valor esperado", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-50">
                  {vr.map((item, index) => (
                    <tr key={index} className={`transition-colors ${index % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-semibold">{item.analito_cod || "—"}</span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">{item.nombre || "—"}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{item.unid || "—"}</td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={item.minimo || ""}
                          onChange={(e) => { const n = [...vr]; n[index].minimo = e.target.value; setVR(n); }}
                          className="w-24 border border-brand-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-brand-400 text-center"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={item.maximo || ""}
                          onChange={(e) => { const n = [...vr]; n[index].maximo = e.target.value; setVR(n); }}
                          className="w-24 border border-brand-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-brand-400 text-center"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-gray-400">Ej: <code>,NO REACTIVO</code></span>
                          <input
                            type="text"
                            value={item.valor_esperado || ""}
                            onChange={(e) => { const n = [...vr]; n[index].valor_esperado = e.target.value; setVR(n); }}
                            className="w-full border border-brand-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-brand-400"
                            placeholder=",NO REACTIVO"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={loadingUp}
                          className="gap-1"
                          onClick={() => actualizarAnalito({
                            especie: selectedEspecie?.value,
                            examen: selectedExamen?.value,
                            equipo: selectedEquipo?.value,
                            reactivo: selectedReactivo?.value,
                            analito_cod: item.analito_cod,
                            minimo: item.minimo,
                            maximo: item.maximo,
                            valor_esperado: item.valor_esperado,
                          })}
                        >
                          <Save className="h-3 w-3" />
                          {loadingUp ? "..." : "Guardar"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {vr.length === 0 && !loading && (
          <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-12 text-center">
            <TestTube2 className="h-12 w-12 text-brand-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Selecciona los parámetros y presiona "Consultar" para ver los analitos</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ParamsPage;
